import {
    fetchAuthSession,
    signOut as amplifySignOut,
    updateUserAttributes as amplifyUpdateUserAttributes,
    fetchUserAttributes,
} from 'aws-amplify/auth';
import store from 'modules/store';
import { local } from 'services';

const strOffset = import.meta.env.VITE_AWS_REFRESH_TOKEN_OFFSET_IN_SEC;
const refreshTokenOffset = parseInt(strOffset || 60, 10);

// Inactivity auto-logout: track last API activity timestamp (0 = first request, skip idle check)
let lastActivityAt = 0;
let isSigningOut = false; // Prevent race conditions during signOut
const INACTIVITY_TIMEOUT = parseInt(import.meta.env.VITE_INACTIVITY_TIMEOUT_IN_SEC || 60 * 30, 10);

// Passive endpoints that don't count as user activity (polling, background sync, etc.)
const PASSIVE_ENDPOINTS = ['/notifications'];

const signOut = () => amplifySignOut();

/**
 * Signs out user if inactive for too long, otherwise updates activity timestamp.
 * @param {string} url - The API endpoint URL
 * @returns {boolean} true if user was signed out
 */
const signOutIfInactive = (url) => {
    // Block all requests once signOut is in progress
    if (isSigningOut) {
        return true;
    }

    const currentTimeSeconds = Math.round(+new Date() / 1000);

    // Check if user has been idle beyond threshold (skip on first request after login)
    if (lastActivityAt > 0) {
        const isIdle = (currentTimeSeconds - lastActivityAt) >= INACTIVITY_TIMEOUT;
        if (isIdle) {
            isSigningOut = true;
            signOut();
            return true;
        }
    }

    // Update activity only for visible tab and non-passive endpoints
    if (!document.hidden) {
        const isPassive = PASSIVE_ENDPOINTS.some((ep) => url?.includes(ep));
        if (!isPassive) {
            lastActivityAt = currentTimeSeconds;
        }
    }

    return false;
};

// For OTP Users returns true - OTP Users phone is already verified
const isUserAttrVerified = (userAttr) =>
    fetchUserAttributes()
        .then((attributes) => {
            const verified = attributes[`${userAttr}_verified`];
            if (verified === true || verified === 'true') {
                return attributes[userAttr];
            }
            return false;
        })
        .catch(() => true);

const updateUserAttribute = async (data) => {
    await amplifyUpdateUserAttributes({ userAttributes: data });
};

const getToken = (url) => new Promise((resolve) => {
    fetchAuthSession()
        .then((session) => {
            if (!session.tokens?.idToken) {
                // Custom Flow fallback
                resolve(local.token());
                return;
            }

            if (signOutIfInactive(url)) {
                resolve(null);
                return;
            }

            const idToken = session.tokens.idToken;
            const idTokenExpire = idToken.payload.exp;
            const currentTimeSeconds = Math.round(+new Date() / 1000);
            const tokenWillExpire = idTokenExpire < currentTimeSeconds + refreshTokenOffset;
            if (tokenWillExpire) {
                fetchAuthSession({ forceRefresh: true })
                    .then((freshSession) => {
                        if (freshSession.tokens?.idToken) {
                            resolve(freshSession.tokens.idToken.toString());
                        } else {
                            signOut();
                        }
                    })
                    .catch(() => signOut());
            } else {
                resolve(idToken.toString());
            }
        })
        .catch(() => {
            // Custom Flow
            resolve(local.token());
        });
});

export const getTokenPayload = (token) => {
    const [, payload] = token.split('.');
    if (payload) { 
        try {
            return JSON.parse(window.atob(payload)); 
        } catch (e) {
            return {};
        }
    }
    return {};
};

export const isCognitoUser = async (token) => {
    const t = token || await getToken();
    if (!t) return false;
    const payload = getTokenPayload(t);
    return !payload.patientId;
};

export const isOTPUser = async (token) => {
    const t = token || await getToken();
    if (!t) return false;
    const payload = getTokenPayload(t);
    return !!payload.patientId;
};

const hasGrant = (roles, user) => {
    let currentUser = { ...user };
    if (!roles.length) return true;
    if (!user) {
        const state = store.getState();
        currentUser = state.user;
    }
        
    const { $role } = currentUser;
    if (!$role) return false;
    
    return roles.includes($role);
};
export { 
    getToken,
    hasGrant,
    isUserAttrVerified,
    updateUserAttribute,
    signOut,
};
